import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, ShieldCheck, ShieldAlert, Clock, Hourglass, FileText, Unlock } from 'lucide-react'
import { consentDisclosureAdvance, declineDisclosureAdvance, requestDisclosureAdvance, getNdaSigningUrl, getDisclosureAgreementContent } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import type { DisclosureAgreement, DisclosureContent } from '../../types'

// Exported so the Matches-tab rung ladder reuses these canonical per-level
// "would reveal" descriptions instead of re-deriving its own copy.
export const STAGE_INFO: Record<number, { name: string; disclosure: string }> = {
  1: { name: 'Anonymous Match', disclosure: 'Topic-level overlap only. No identity information shared.' },
  2: { name: 'Aggregated Stats', disclosure: 'Aggregate statistics on overlapping areas. No individual documents revealed.' },
  3: { name: 'Categorical Profile', disclosure: 'Category-level research profile. Organisation identity still hidden.' },
  4: { name: 'Pattern Insights', disclosure: 'Identity exchange. Both organisations can see who they are collaborating with.' },
  5: { name: 'Structured Summary', disclosure: 'AI-generated summary of overlapping research with key claims. PII redacted.' },
  6: { name: 'Full Collaboration', disclosure: 'Full access to shared documents and resources. Auto-accepted connection created.' },
}

// Human labels for the DisclosureContent fields, in STAGE_FIELDS unlock order
// (filters.py) — every field present here is real, already-unlocked content
// (filter_response's whitelist), never a placeholder for a locked one.
const CONTENT_FIELD_LABELS: Array<[keyof DisclosureContent, string]> = [
  ['similarity_band', 'Similarity'],
  ['domain_category', 'Domain'],
  ['aggregate_similarity_score', 'Match score'],
  ['topic_overlap_count', 'Overlapping topics'],
  ['org_sector', 'Sector'],
  ['technology_domain', 'Technology domain'],
  ['capability_categories', 'Capabilities'],
  ['org_name', 'Organisation'],
  ['topics', 'Topics'],
  ['field', 'Field'],
  ['keywords', 'Keywords'],
  ['complementarity_score', 'Complementarity'],
  ['ai_summary', 'AI summary'],
  ['key_claims_redacted', 'Key claims'],
]

function contentRows(c: DisclosureContent | undefined): Array<{ label: string; value: string }> {
  if (!c) return []
  const rows: Array<{ label: string; value: string }> = []
  for (const [key, label] of CONTENT_FIELD_LABELS) {
    const v = c[key]
    if (v === undefined || v === null) continue
    if (typeof v === 'number') {
      rows.push({ label, value: key.toString().includes('score') || key === 'similarity_band' ? String(v) : String(v) })
    } else if (Array.isArray(v)) {
      if (v.length === 0) continue
      const text = v.map((item) => (typeof item === 'string' ? item : (item as { theme: string }).theme)).join(' · ')
      rows.push({ label, value: text })
    } else if (typeof v === 'string') {
      rows.push({ label, value: v })
    }
  }
  // aggregate_similarity_score / complementarity_score are fractions (0-1) — show as %.
  return rows.map((r) =>
    r.label === 'Match score' || r.label === 'Complementarity'
      ? { ...r, value: `${Math.round(Number(r.value) * 100)}%` }
      : r,
  )
}

interface DisclosureRequestModalProps {
  isOpen: boolean
  onClose: () => void
  agreement: DisclosureAgreement
  onConsent: () => void
  onReject: () => void
}

export default function DisclosureRequestModal({
  isOpen,
  onClose,
  agreement,
  onConsent,
  onReject,
}: DisclosureRequestModalProps) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [ndaLoading, setNdaLoading] = useState(false)
  const [ndaFallbackUrl, setNdaFallbackUrl] = useState<string | null>(null)
  const nextLevel = agreement.current_level + 1

  // The real, level-gated disclosed content — this is what makes "View" show
  // something rather than repeating the promise text (Bug: reveal layer
  // delivered no payload at any rung). Fetched fresh per agreement/level so a
  // just-consented advance shows its newly-unlocked fields immediately.
  const { data: contentData, isFetching: contentLoading } = useQuery({
    queryKey: ['disclosureAgreementContent', agreement.agreement_id, agreement.current_level],
    queryFn: () => getDisclosureAgreementContent(agreement.agreement_id),
    enabled: isOpen,
  })
  const revealedRows = contentRows(contentData?.content)

  // The 3→4 advance is NDA-gated: a plain "Request Advance" here only trips the gate
  // (no request is recorded) and previously showed a misleading "Requesting…" spinner.
  // When the NDA isn't completed, surface the NDA action directly instead.
  const ndaRequired = nextLevel === 4 && agreement.nda_status !== 'completed'
  const iSignedNda = agreement.my_nda_signed === true
  const handleSignNda = async () => {
    setNdaLoading(true)
    setErrorMsg(null)
    setNdaFallbackUrl(null)
    // Open the window synchronously BEFORE any await to preserve the user-gesture
    // context. Browsers block window.open called after an async boundary.
    // BUG-098: do NOT pass 'noopener'/'noreferrer' here — with either feature
    // window.open returns null even when the tab opens, so this code always
    // took the popup-blocked fallback AND left a stray blank tab behind. Open
    // with an opener handle, then sever it manually after navigation below.
    const w = window.open('', '_blank')
    try {
      // Provision (or re-provision) the NDA envelope BEFORE fetching a signing URL.
      // The L3→L4 advance is NDA-gated: requesting it makes the backend raise
      // NdaRequired and create the DocuSign envelope (_handle_nda_required).
      // We always call this — even when nda_status='pending' — because the fixture
      // or a partial write can leave nda_status='pending' with no nda_envelope_id,
      // which causes getNdaSigningUrl to 400. The backend handles re-requests
      // gracefully: if a valid envelope already exists it skips creation; if not
      // (e.g. test fixture) it creates one now.
      await requestDisclosureAdvance({ agreementId: agreement.agreement_id })
      invalidate()
      const { url } = await getNdaSigningUrl(agreement.agreement_id)
      if (w && !w.closed) {
        w.location.href = url
        // BUG-098: null the opener AFTER navigation so the DocuSign tab cannot
        // script its opener (the tab-nabbing protection 'noopener' provided,
        // without the null return that broke the flow above).
        try { w.opener = null } catch { /* navigation already detached it */ }
      } else {
        // Popup was blocked even with the synchronous open — show a clickable fallback.
        setNdaFallbackUrl(url)
      }
    } catch (err) {
      // Do NOT close w — the blank tab stays open as the user's indication that
      // Sign NDA was attempted. Closing it would confuse users who briefly saw it.
      setErrorMsg(err instanceof Error ? err.message : 'Could not open the NDA for signing.')
    } finally {
      setNdaLoading(false)
    }
  }

  // Role-relative consent state. Prefer the backend-provided perspective fields;
  // fall back to deriving them from the raw org levels + the caller's active org.
  const myOrg = user?.active_org_id
  const myConsented =
    agreement.my_consented_level ??
    (myOrg && myOrg === agreement.org_a_id
      ? agreement.org_a_consented_level
      : myOrg && myOrg === agreement.org_b_id
        ? agreement.org_b_consented_level
        : undefined)
  const counterpartyConsented =
    agreement.counterparty_consented_level ??
    (myOrg && myOrg === agreement.org_a_id
      ? agreement.org_b_consented_level
      : myOrg && myOrg === agreement.org_b_id
        ? agreement.org_a_consented_level
        : undefined)

  const known = myConsented !== undefined && counterpartyConsented !== undefined
  // I have already consented/requested this advance — my counterparty must act.
  const iAlreadyRequested = known && (myConsented as number) > (counterpartyConsented as number)
  // My counterparty has requested — it is my turn to consent.
  const counterpartyRequested = known && (counterpartyConsented as number) > (myConsented as number)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['disclosure-agreements'] })
    queryClient.invalidateQueries({ queryKey: ['disclosureAgreements'] })
  }
  // Write the authoritative agreement the mutation returned straight into the
  // cache (BUG-M). Previously we only invalidated, so the UI fell back to an
  // eventually-consistent GSI refetch that could momentarily show the OLD level
  // — the "advanced then reverted" flicker. setQueryData makes the new level
  // stick immediately; the background refetch from invalidate() then reconciles.
  const applyToCaches = (updated: DisclosureAgreement) => {
    queryClient.setQueriesData<DisclosureAgreement[]>(
      {
        predicate: (q) =>
          q.queryKey[0] === 'disclosure-agreements' || q.queryKey[0] === 'disclosureAgreements',
      },
      (old) => {
        if (!old) return old
        let found = false
        const next = old.map((a) => {
          if (a.agreement_id === updated.agreement_id) {
            found = true
            return updated
          }
          return a
        })
        return found ? next : [...next, updated]
      },
    )
  }
  const handleSuccess = (updated: DisclosureAgreement) => {
    setErrorMsg(null)
    applyToCaches(updated)
    invalidate()
    onConsent()
    onClose()
  }
  const handleError = (err: unknown) => {
    // BUG-E fix: surface the failure instead of swallowing it (the old modal had
    // no onError, so a 400 looked like "nothing happened").
    setErrorMsg(err instanceof Error ? err.message : 'Request failed. Please try again.')
  }

  const consentMutation = useMutation({
    mutationFn: () => consentDisclosureAdvance(agreement.agreement_id),
    onSuccess: handleSuccess,
    onError: handleError,
  })
  const requestMutation = useMutation({
    mutationFn: () => requestDisclosureAdvance({ agreementId: agreement.agreement_id }),
    onSuccess: handleSuccess,
    onError: handleError,
  })
  // B1-01: a real server-side decline of the counterparty's pending advance.
  // handleSuccess applies the returned (cleared) agreement to caches and closes,
  // so the modal and Home agree — no client-only dismissal.
  const declineMutation = useMutation({
    mutationFn: () => declineDisclosureAdvance(agreement.agreement_id),
    onSuccess: handleSuccess,
    onError: handleError,
  })

  // Open at ANY level, including 6. Previously this bailed when nextLevel > 6, so
  // at Level 6 (Full Collaboration) clicking "View" did nothing and the history /
  // identity were unviewable. At max level we still show status, identity and
  // history; only the advance action is hidden.
  if (!isOpen) return null

  const atMaxLevel = nextLevel > 6
  const nextStage = STAGE_INFO[nextLevel]
  const currentStage = STAGE_INFO[agreement.current_level]
  const busy = consentMutation.isPending || requestMutation.isPending || declineMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Disclosure Advance Request</h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Current → Next level */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <div className="text-xs text-gray-500">Current Level</div>
              <div className="text-sm font-medium text-gray-700">
                {agreement.current_level}. {currentStage?.name}
              </div>
            </div>
            <ShieldCheck size={16} className="text-gray-500" />
            <div className="flex-1">
              <div className="text-xs text-gray-500">{atMaxLevel ? 'Status' : 'Next Level'}</div>
              <div className="text-sm font-medium text-iflow-600">
                {atMaxLevel ? 'Full Collaboration — top level reached' : `${nextLevel}. ${nextStage?.name}`}
              </div>
            </div>
          </div>

          {/* Real disclosed content at the current rung — not the promise text */}
          {contentLoading ? (
            <div className="p-3 border border-gray-200 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Loading disclosed content…</p>
            </div>
          ) : revealedRows.length > 0 ? (
            <div className="p-3 border border-iflow-200 bg-iflow-50 rounded-lg" data-testid="disclosure-revealed-content">
              <div className="flex items-center gap-1.5 mb-2">
                <Unlock size={14} className="text-iflow-600" />
                <div className="text-sm font-medium text-iflow-800">
                  Disclosed at {currentStage?.name ?? `Level ${agreement.current_level}`}
                </div>
              </div>
              <dl className="space-y-1">
                {revealedRows.map((r) => (
                  <div key={r.label} className="flex items-start gap-2 text-xs">
                    <dt className="font-medium text-iflow-700 shrink-0">{r.label}:</dt>
                    <dd className="text-iflow-900 min-w-0">{r.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}

          {/* L4+ identity exchange — who you are collaborating with (BUG 3d) */}
          {agreement.current_level >= 4 && (agreement.counterparty_name || agreement.counterparty_email) && (
            <div className="p-3 border border-emerald-200 bg-emerald-50 rounded-lg">
              <div className="flex items-start gap-2">
                <ShieldCheck size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-emerald-800">Collaborating with</div>
                  <p className="text-xs text-emerald-700 mt-1">
                    {agreement.counterparty_name || agreement.counterparty_email}
                    {agreement.counterparty_name && agreement.counterparty_email
                      ? ` · ${agreement.counterparty_email}`
                      : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* NDA gate notice (3→4) — takes precedence over the generic disclosure copy */}
          {!atMaxLevel && ndaRequired && (
            <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
              <div className="flex items-start gap-2">
                <FileText size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-amber-800">Mutual NDA required</div>
                  <p className="text-xs text-amber-700 mt-1">
                    {iSignedNda
                      ? 'You have signed the mutual NDA. Level 4 (identity reveal) unlocks once your counterparty signs too.'
                      : 'Level 4 reveals identities, so both parties must sign a mutual NDA before advancing.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* What will be disclosed (only when there is a next level and no NDA gate) */}
          {!atMaxLevel && !ndaRequired && (
            <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
              <div className="flex items-start gap-2">
                <ShieldAlert size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-amber-800">What will be disclosed</div>
                  <p className="text-xs text-amber-700 mt-1">{nextStage?.disclosure}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bilateral status hint */}
          {iAlreadyRequested && (
            <div className="p-3 border border-sky-200 bg-sky-50 rounded-lg">
              <p className="text-xs text-sky-700">
                You have requested this advance. Both organisations must consent — your
                counterparty needs to approve before the level changes.
              </p>
            </div>
          )}

          {/* History timeline */}
          {(agreement.history || []).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                History
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1.5">
                {(agreement.history || [])
                  .reduce<typeof agreement.history>((acc, entry) => {
                    const prev = acc[acc.length - 1]
                    if (
                      prev &&
                      prev.action === entry.action &&
                      prev.from_level === entry.from_level &&
                      prev.to_level === entry.to_level
                    ) return acc
                    return [...acc, entry]
                  }, [])
                  .map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <Clock size={10} className="text-gray-500 shrink-0" />
                    <span className="font-mono text-xs text-gray-500">
                      {new Date(entry.timestamp).toLocaleDateString('en-AU')}
                    </span>
                    <span>
                      {entry.action === 'created' && 'Agreement created'}
                      {entry.action === 'request_advance' && `Level ${entry.from_level} → ${entry.to_level} requested`}
                      {entry.action === 'consent_advance' && `Consented to level ${entry.to_level}`}
                      {entry.action === 'level_advanced' && `Advanced to level ${entry.to_level}`}
                      {entry.action === 'revoked' && `Revoked to level ${entry.to_level}`}
                      {entry.action === 'nda_completed' && 'Mutual NDA signed by both parties'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="p-3 border border-red-200 bg-red-50 rounded-lg">
              <p className="text-xs text-red-700">{errorMsg}</p>
            </div>
          )}
          {ndaFallbackUrl && !errorMsg && (
            <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg" data-testid="nda-fallback-link">
              <p className="text-xs text-amber-800">
                Popup was blocked.{' '}
                <a
                  href={ndaFallbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  Click here to open the NDA signing page.
                </a>
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => {
              // Only a pending advance FROM the counterparty can be declined
              // server-side (B1-01). Otherwise there is nothing to reject — just
              // dismiss (keeps the old behaviour for the requester / view cases).
              if (counterpartyRequested && !atMaxLevel) { declineMutation.mutate(); return }
              if (!atMaxLevel) onReject()
              onClose()
            }}
            disabled={busy}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {atMaxLevel ? 'Close' : (counterpartyRequested && declineMutation.isPending ? 'Declining…' : 'Decline')}
          </button>

          {!atMaxLevel && (ndaRequired ? (
            iSignedNda ? (
              <button
                disabled
                title="You have signed the mutual NDA — your counterparty must sign before Level 4 unlocks."
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg cursor-not-allowed"
              >
                <Hourglass size={14} /> Awaiting counterparty NDA
              </button>
            ) : (
              <button
                onClick={handleSignNda}
                disabled={ndaLoading}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                <FileText size={14} />
                {ndaLoading ? 'Opening…' : 'Sign NDA'}
              </button>
            )
          ) : iAlreadyRequested ? (
            <button
              disabled
              title="You have requested this advance — your counterparty must consent."
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg cursor-not-allowed"
            >
              <Hourglass size={14} /> Awaiting counterparty consent
            </button>
          ) : counterpartyRequested ? (
            <button
              onClick={() => consentMutation.mutate()}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
            >
              <ShieldCheck size={14} />
              {busy ? 'Processing…' : 'Consent to Advance'}
            </button>
          ) : (
            <button
              onClick={() => requestMutation.mutate()}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
            >
              <ShieldCheck size={14} />
              {busy ? 'Requesting…' : `Request Advance to Level ${nextLevel}`}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
